import { AvatarSizes, useIntl, WithSkeleton } from '@capsicum/common';
import { BaseCheckboxInput, Image, Label, ListCard, SkeletonItem } from '@capsicum/common-web';
import { type WCPledgeTeam } from '@capsicum/features/world-championship-pledge/shared';
import { noop } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components';

export type CountryCardProps = {
	country: WCPledgeTeam;
	handleCountryCheckboxChange: (teamId: string, ftName: string) => () => void;
	hasPrice?: boolean;
	isDisabled: boolean;
	isDisqualified: boolean;
	isSelected: boolean;
};

const NoCheckboxLabel = ({ countryName, isDisqualified }: { countryName: string; isDisqualified: boolean }) => {
	const { $t } = useIntl();

	return isDisqualified ? (
		<Label
			label={$t('worldChampionshipPledge.tokenTab.cardStatusDisqualified')}
			size="condensed"
			testID={`wcpledge-tokens-tab-${countryName}-disqualified-label`}
			variant="grey"
		/>
	) : (
		<Label
			label={$t('worldChampionshipPledge.tokenTab.cardStatusUnstaking')}
			size="condensed"
			testID={`wcpledge-tokens-tab-${countryName}-unstakingCHZ-label`}
			variant="grey"
		/>
	);
};

export const WCPledgeCountryCardComponent = ({
	country,
	handleCountryCheckboxChange,
	isDisabled,
	isDisqualified,
	isSelected
}: CountryCardProps) => {
	const handleClick = useMemo(
		() => handleCountryCheckboxChange(country.id, country.ftName),
		[country.id, country.ftName, handleCountryCheckboxChange]
	);

	const hasCheckbox = country.canUserPledge && !isDisqualified;

	return (
		<S.CountryCard
			$isDisabled={isDisabled}
			$isSelected={isSelected}
			data-testid={`wcpledge-country-card-${country.name}`}
			key={country.name}
			onClick={!isDisabled && hasCheckbox ? handleClick : noop}
		>
			<S.Container>
				<S.StartContainer>
					<Image
						alt={`${country.name} flag`}
						height={AvatarSizes.S}
						src={country.flagUrl}
						width={AvatarSizes.S}
					/>
				</S.StartContainer>
				<S.CardBody>
					<ListCard.Row>
						<ListCard.Title>{country.name}</ListCard.Title>
					</ListCard.Row>
				</S.CardBody>
				<S.EndContainer>
					{hasCheckbox ? (
						<BaseCheckboxInput
							columnAlign="center"
							disabled={isDisabled}
							isStateHandledOutside
							testID={`wcpledge-tokens-tab-${country.name}`}
							value={isSelected}
							onChange={noop}
						/>
					) : (
						<NoCheckboxLabel countryName={country.name} isDisqualified={isDisqualified} />
					)}
				</S.EndContainer>
			</S.Container>
		</S.CountryCard>
	);
};

const WCPledgeCountryCardSkeleton = () => (
	<S.CountryCard $isDisabled={false} $isSelected={false}>
		<S.Container>
			<S.StartContainer>
				<ListCard.Image isLoading />
			</S.StartContainer>
			<S.CardBody>
				<ListCard.Row>
					<SkeletonItem borderRadius={2} height={20} width={108} />
				</ListCard.Row>
			</S.CardBody>
			<S.EndContainer>
				<SkeletonItem borderRadius={2} height={20} width={20} />
			</S.EndContainer>
		</S.Container>
	</S.CountryCard>
);

export const WCPledgeCountryCard = React.memo(
	WithSkeleton(WCPledgeCountryCardComponent, WCPledgeCountryCardSkeleton),
	(prev, next) =>
		prev.isLoading === next.isLoading &&
		prev.isDisabled === next.isDisabled &&
		prev?.country?.id === next?.country?.id &&
		prev.isSelected === next.isSelected &&
		prev.isDisqualified === next.isDisqualified
);

namespace S {
	export const CountryCard = styled(ListCard)<{ $isDisabled: boolean; $isSelected: boolean }>`
		display: flex;
		cursor: ${({ $isDisabled }) => ($isDisabled ? 'default' : 'pointer')};
		flex-direction: column;
		align-items: flex-start;
		width: 100%;
		border: ${({ $isSelected, theme }) =>
			$isSelected ? `1px solid ${theme.colors.borders.brand.enabled}` : `1px solid transparent`};
		opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: row;
		width: 100%;
		height: 100%;
		padding: ${({ theme }) => `${theme.spacings.small} ${theme.spacings.xxsmall}`};
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const StartContainer = styled(ListCard.Start)`
		align-items: flex-start;
		align-items: center;
		padding: 0;
	`;

	export const CardBody = styled(ListCard.Content)`
		padding: 0;
	`;

	export const Subtitle = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: baseline;
		color: ${({ theme }) => theme.colors.text.main.subtle};
		width: 100%;
		${({ theme }) => theme.fonts.text.text5}
	`;

	export const EndContainer = styled(ListCard.End)`
		align-items: flex-end;
		align-items: center;
	`;
}
